/**
 * Chart Data Generators
 *
 * Reusable data generation utilities for sparklines and valuation bands.
 * These functions are decoupled from the UI layer and can be used in:
 * - React components
 * - Server-side rendering
 * - API endpoints
 * - Automated reporting
 * - AI/LLM data generation workflows
 */

export interface SparklineData {
  x: number[]
  y: number[]
  w: number
  h: number
}

/**
 * Generates sparkline chart data with simulated variance
 *
 * @param points - Number of data points to generate
 * @param height - Height of the chart in pixels
 * @returns Sparkline data with x/y coordinates and dimensions
 */
export function generateSparkline(points: number, height: number): SparklineData {
  const w = 760
  const xs: number[] = []
  const ys: number[] = []
  let base = 12000

  for (let i = 0; i < points; i++) {
    const step = (Math.random() - 0.4) * 600
    base = Math.max(2000, base + step + i * 30)
    xs.push((i / (points - 1)) * w)
    ys.push(height - (base % height))
  }

  return { x: xs, y: ys, w, h: height }
}

export interface ValuationBandsData {
  p25: number[]
  p75: number[]
  w: number
  h: number
}

/**
 * Generates valuation band data (25th and 75th percentile lines)
 *
 * @returns Valuation band data with p25/p75 arrays and dimensions
 */
export function generateValuationBands(): ValuationBandsData {
  const w = 760
  const h = 160

  const line = (seed: number): number[] => {
    let base = seed
    const arr: number[] = []
    for (let i = 0; i < 48; i++) {
      base += (Math.random() - 0.5) * 120
      arr.push(base)
    }
    return arr
  }

  return { p25: line(3000), p75: line(3600), w, h }
}

/**
 * Runtime smoke tests for chart generators
 * These help catch regressions during development
 */
export function runChartGeneratorTests(): void {
  const assert = (cond: boolean, msg: string) => {
    if (!cond) console.error('[chartGenerators] ❌', msg)
  }

  try {
    const s = generateSparkline(10, 100)
    assert(s.x.length === 10 && s.y.length === 10, 'sparkline length = 10')
    assert(s.y.some(v => v !== s.y[0]), 'sparkline has variance')

    const b = generateValuationBands()
    assert(b.p25.length === 48 && b.p75.length === 48, 'valuation bands length = 48')

    console.log('[chartGenerators] ✅ smoke tests passed')
  } catch (e) {
    console.error('[chartGenerators] ⚠️ error running tests', e)
  }
}
